import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CreateGoal from './CreateGoal';
import AssignGoal from './AssignGoal';
import { FiChevronDown } from 'react-icons/fi';
import './../Styles/Admin.css';

const apiUrl = process.env.REACT_APP_API_URL;

const EmployeeDirectoryTable = ({
    title = 'List of Employees',
    employees = [],
    onViewDetails,
    showGoalActions = false,
    showProjectFilter = false,
    showRoleFilter = false,
    availableRoles = ['all', 'lead', 'employee'],
    projects: propProjects,
    loggedInManagerId,
    isMainManagerFlag = 0,
    showSearch = false
}) => {
    const [projects, setProjects] = useState(propProjects || []);
    const [selectedProject, setSelectedProject] = useState('all');
    const [selectedRole, setSelectedRole] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showCreateGoalDialog, setShowCreateGoalDialog] = useState(false);
    const [showAssignGoalDialog, setShowAssignGoalDialog] = useState(false);

    // Fetch projects if filter is enabled and not passed as prop
    useEffect(() => {
        if (showProjectFilter && !propProjects) {
            axios.get(`${apiUrl}/api/fetch-projects`)
                .then(res => setProjects(res.data || []))
                .catch(err => console.error('Error fetching projects:', err));
        }
    }, [showProjectFilter, propProjects]);

    // Handle outside clicks to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.goals-dropdown')) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const toggleDropdown = (name) => {
        setActiveDropdown(prev => prev === name ? null : name);
    };

    // Filter employees by search, role, and project
    const filteredEmployees = employees.filter((employee) => {
        const matchesSearch = !searchTerm || (
            employee.full_name && employee.full_name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesRole = selectedRole === 'all' || (
            employee.role && employee.role.toLowerCase() === selectedRole.toLowerCase()
        );

        const matchesProject = selectedProject === 'all' || (
            (employee.project_ids && employee.project_ids.includes(selectedProject)) ||
            (employee.project_name && employee.project_name === selectedProject)
        );

        return matchesSearch && matchesRole && matchesProject;
    });

    const formatRoleLabel = (role) => {
        if (typeof role === 'object') return role;
        if (role === 'all') return { id: 'all', label: 'All' };
        if (role === 'admin') return { id: 'admin', label: 'Manager' };
        if (role === 'lead') return { id: 'lead', label: 'Lead' };
        if (role === 'employee') return { id: 'employee', label: 'Employee' };
        return { id: role, label: role.charAt(0).toUpperCase() + role.slice(1) };
    };

    return (
        <div className="employee-list-section">
            <div className="employee-controls">
                <div className="controls-left">
                    <h2>{title}</h2>

                    {showGoalActions && (
                        <div className={`goals-dropdown ${activeDropdown === 'goals' ? 'active' : ''}`}>
                            <button className="dropbtn" onClick={() => toggleDropdown('goals')}>
                                <span>Goals</span>
                                <FiChevronDown className="dropdown-chevron" size={14} />
                            </button>
                            <div className="goals-dropdown-content scrollable-dropdown">
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowCreateGoalDialog(true);
                                        setActiveDropdown(null);
                                    }}
                                >
                                    Create Goals
                                </a>
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowAssignGoalDialog(true);
                                        setActiveDropdown(null);
                                    }}
                                >
                                    Assign Goals
                                </a>
                            </div>
                        </div>
                    )}

                    {showProjectFilter && (
                        <div className={`goals-dropdown ${activeDropdown === 'projects' ? 'active' : ''}`}>
                            <button className="dropbtn" onClick={() => toggleDropdown('projects')}>
                                <span>Projects</span>
                                <FiChevronDown className="dropdown-chevron" size={14} />
                            </button>
                            <div className="goals-dropdown-content scrollable-dropdown">
                                <a
                                    href="#"
                                    className={selectedProject === 'all' ? 'selected-filter' : ''}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setSelectedProject('all');
                                        setActiveDropdown(null);
                                    }}
                                >
                                    <span>All</span>
                                    {selectedProject === 'all' && <span>✓</span>}
                                </a>
                                {projects.map((proj) => {
                                    const projId = proj._id || proj.id;
                                    const projName = proj.project_name || proj.name || projId;
                                    return (
                                        <a
                                            href="#"
                                            key={projId}
                                            className={selectedProject === projId ? 'selected-filter' : ''}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedProject(projId);
                                                setActiveDropdown(null);
                                            }}
                                        >
                                            <span>{projName}</span>
                                            {selectedProject === projId && <span>✓</span>}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {showRoleFilter && (
                        <div className={`goals-dropdown ${activeDropdown === 'roles' ? 'active' : ''}`}>
                            <button className="dropbtn" onClick={() => toggleDropdown('roles')}>
                                <span>Roles</span>
                                <FiChevronDown className="dropdown-chevron" size={14} />
                            </button>
                            <div className="goals-dropdown-content scrollable-dropdown">
                                {availableRoles.map((r) => {
                                    const { id, label } = formatRoleLabel(r);
                                    return (
                                        <a
                                            href="#"
                                            key={id}
                                            className={selectedRole === id ? 'selected-filter' : ''}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedRole(id);
                                                setActiveDropdown(null);
                                            }}
                                        >
                                            <span>{label}</span>
                                            {selectedRole === id && <span>✓</span>}
                                        </a>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <div className="controls-right">
                    {showSearch && (
                        <input
                            type="text"
                            placeholder="Search Employees..."
                            className="search-bar"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    )}
                </div>
            </div>

            <div className="employee-table-wrapper">
                <table className="employee-table">
                    <thead>
                        <tr>
                            <th>Employee Number</th>
                            <th>Full Name</th>
                            <th>Role</th>
                            <th>Job Title</th>
                            <th>Department</th>
                            <th>Project Name</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEmployees.length > 0 ? (
                            filteredEmployees.map((employee) => (
                                <tr key={employee.employee_number || employee._id}>
                                    <td>{employee.employee_number || 'N/A'}</td>
                                    <td>{employee.full_name || employee.name || 'N/A'}</td>
                                    <td style={{ textTransform: 'capitalize' }}>{employee.role || 'N/A'}</td>
                                    <td>{employee.job_title || 'N/A'}</td>
                                    <td>{employee.department || 'N/A'}</td>
                                    <td>{employee.project_name || 'N/A'}</td>
                                    <td>
                                        <button
                                            className="details-button"
                                            onClick={() => onViewDetails && onViewDetails(employee)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                    No employees found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Modals for Create Goal & Assign Goal */}
            {showCreateGoalDialog && (
                <CreateGoal onClose={() => setShowCreateGoalDialog(false)} />
            )}
            {showAssignGoalDialog && (
                <AssignGoal
                    loggedInManagerId={loggedInManagerId}
                    isMainManagerFlag={isMainManagerFlag}
                    onClose={() => setShowAssignGoalDialog(false)}
                />
            )}
        </div>
    );
};

export default EmployeeDirectoryTable;
